import React, { useEffect, useRef } from 'react';

import type { BoardOperation } from './editor';
import { type Block, type Row, isColumn } from './types';
import type { BoardEditor } from './useBoardEdit';

type RowSplitterProps = {
  row: Row;
  index: number;
  spans: Array<number>;
  host: React.RefObject<HTMLDivElement>;
  editor: BoardEditor;
  onPreview: (spans: Array<number> | undefined) => void;
};

const MIN_SPAN = 2;
const STRONG_SPANS = [3, 4, 6, 8, 9];
const STRONG_SNAP_PX = 6;

function snapped(span: number, unitPx: number, free: boolean): number {
  const fine = Math.round(span * 10) / 10;
  if (free) return fine;
  return STRONG_SPANS.find((strong) => Math.abs(strong - span) * unitPx <= STRONG_SNAP_PX) ?? fine;
}

export const widthLabel = (span: number) => `Width ${Number.isInteger(span) ? span : Math.round(span * 10) / 10} of 12 columns`;

const carrier = (item: Row[number]): Block => (isColumn(item) ? item[0] : item);

export function resizeOperations(row: Row, spans: Array<number>): Array<BoardOperation> {
  return row.flatMap((item, index) => {
    const blocks = isColumn(item) ? item : [item];
    const others = blocks
      .slice(1)
      .filter((block) => block.w)
      .map((block): BoardOperation => ({ op: 'set', id: block.id, patch: { w: null } }));
    return carrier(item).w === spans[index]
      ? others
      : [{ op: 'set', id: carrier(item).id, patch: { w: spans[index] } }, ...others];
  });
}

export default function RowSplitter({ row, index, spans, host, editor, onPreview }: RowSplitterProps) {
  const drag = useRef<{ left: number; width: number; base: Array<number>; next?: Array<number> } | undefined>(
    undefined,
  );
  const shared = index < row.length - 1;
  const cancel = () => {
    if (!drag.current) return;
    drag.current = undefined;
    onPreview(undefined);
  };
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !drag.current) return;
      event.stopPropagation();
      cancel();
    };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, []);
  const spansAt = (clientX: number, free: boolean) => {
    const at = drag.current!;
    const spans = at.base;
    const start = spans.slice(0, index).reduce((sum, span) => sum + span, 0);
    const width = snapped(((clientX - at.left) / at.width) * 12 - start, at.width / 12, free);
    if (shared) {
      const pair = spans[index] + spans[index + 1];
      const left = Math.min(Math.max(width, MIN_SPAN), pair - MIN_SPAN);
      return spans.map((span, position) => (position === index ? left : position === index + 1 ? pair - left : span));
    }
    const room = 12 - spans.reduce((sum, span, position) => (position === index ? sum : sum + span), 0);
    return spans.map((span, position) =>
      position === index ? Math.min(Math.max(width, MIN_SPAN), room) : span,
    );
  };

  return (
    <span
      aria-hidden
      className={`splitter${shared ? ' shared' : ''}`}
      onLostPointerCapture={cancel}
      onPointerCancel={cancel}
      onPointerDown={(event) => {
        const box = host.current?.getBoundingClientRect();
        if (!box || editor.pending) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = { left: box.left, width: box.width, base: spans };
        onPreview(spans);
      }}
      onPointerMove={(event) => {
        if (!drag.current) return;
        drag.current.next = spansAt(event.clientX, event.altKey);
        onPreview(drag.current.next);
      }}
      onPointerUp={() => {
        const { base, next } = drag.current ?? {};
        drag.current = undefined;
        onPreview(undefined);
        if (base && next && next.some((span, position) => span !== base[position]))
          editor.apply(resizeOperations(row, next));
      }}
    />
  );
}
